import React, { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountTree as AccountTreeIcon,
  ArrowBack as ArrowBackIcon,
  GridView as GridViewIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  OpenInNew as OpenInNewIcon,
  YouTube as YouTubeIcon,
} from "@mui/icons-material";
import apiService, {
  type ContentTaxonomyChunkRow,
  type ContentTaxonomyChunksResponse,
} from "../services/api";
import {
  podcastContentAnalysisPath,
} from "../constants/reviewRoutePaths";
import { youtubeWatchUrlFromWatchUrl } from "../utils/youtubeVideoId";
import TopicNetworkGraph from "./taxonomy/TopicNetworkGraph";
import TopicTreemap from "./taxonomy/TopicTreemap";
import SubtopicChunksPanel from "./taxonomy/SubtopicChunksPanel";
import {
  filterChunksForSubtopic,
  type TreemapSubtopicSelection,
} from "./taxonomy/subtopicChunkMatch";
import {
  buildTopicHierarchy,
  hierarchyHasSubtopics,
  type DominanceLevel,
} from "./taxonomy/taxonomyHierarchy";

type DominanceView = "treemap" | "network";

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function actionColor(action: ContentTaxonomyChunkRow["action"]): "success" | "warning" | "default" {
  if (action === "accept") return "success";
  if (action === "reexamine") return "warning";
  return "default";
}

function actionLabel(action: ContentTaxonomyChunkRow["action"]): string {
  if (action === "accept") return "accepted";
  if (action === "reexamine") return "rejected (re-exam)";
  return "discarded";
}

function TopicChips({ chunk }: { chunk: ContentTaxonomyChunkRow }) {
  if (!chunk.resolved?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {chunk.resolved.map((topic) => {
        const mainLabel =
          topic.outcome === "merged" && topic.originalSlug !== topic.slug
            ? `${topic.display} (← ${topic.originalSlug})`
            : topic.display;
        return (
          <Tooltip
            key={`${topic.originalSlug}-${topic.slug}-main`}
            title={`${topic.slug} — ${topic.criterion}${topic.similarity != null ? ` (${topic.similarity.toFixed(2)})` : ""}`}
          >
            <Chip
              size="small"
              label={mainLabel}
              color={topic.outcome === "review" ? "warning" : "default"}
              variant={topic.outcome === "merged" ? "outlined" : "filled"}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function SubtopicChips({ chunk }: { chunk: ContentTaxonomyChunkRow }) {
  const subtopics =
    chunk.resolved?.filter((topic) => topic.subtopic || topic.subtopicDisplay) ?? [];

  if (subtopics.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {subtopics.map((topic) => {
        const label = topic.subtopicDisplay ?? topic.subtopic ?? "";
        const slug = topic.subtopic ?? "";
        return (
          <Tooltip
            key={`${topic.slug}-${slug}`}
            title={`${label}${slug ? ` [${slug}]` : ""} · parent: ${topic.slug}`}
          >
            <Chip size="small" label={label} variant="outlined" color="secondary" />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

export type EpisodeSubtopicGroup = {
  parentTopicSlug: string;
  parentDisplay?: string;
  subtopics: NonNullable<ContentTaxonomyChunksResponse["episodeSubtopics"]>;
};

export function groupEpisodeSubtopics(
  episodeSubtopics: ContentTaxonomyChunksResponse["episodeSubtopics"],
  episodeTopics?: ContentTaxonomyChunksResponse["episodeTopics"],
): EpisodeSubtopicGroup[] {
  if (!episodeSubtopics?.length) return [];

  const parentDisplayBySlug = new Map(
    (episodeTopics ?? []).map((topic) => [topic.slug, topic.display]),
  );
  const grouped = new Map<string, EpisodeSubtopicGroup>();

  for (const subtopic of episodeSubtopics) {
    const existing = grouped.get(subtopic.parentTopicSlug);
    if (existing) {
      existing.subtopics.push(subtopic);
    } else {
      grouped.set(subtopic.parentTopicSlug, {
        parentTopicSlug: subtopic.parentTopicSlug,
        parentDisplay: parentDisplayBySlug.get(subtopic.parentTopicSlug),
        subtopics: [subtopic],
      });
    }
  }

  return Array.from(grouped.values()).sort((a, b) => {
    const mentionsA = a.subtopics.reduce((sum, s) => sum + s.mentions, 0);
    const mentionsB = b.subtopics.reduce((sum, s) => sum + s.mentions, 0);
    return mentionsB - mentionsA;
  });
}

const TRANSCRIPT_PREVIEW_CHARS = 120;

function ChunkTranscriptPreview({ text, onView }: { text: string; onView: () => void }) {
  const preview =
    text.length > TRANSCRIPT_PREVIEW_CHARS
      ? `${text.slice(0, TRANSCRIPT_PREVIEW_CHARS)}…`
      : text;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
        {preview}
      </Typography>
      {text.length > TRANSCRIPT_PREVIEW_CHARS && (
        <Button size="small" onClick={onView} sx={{ mt: 0.5, p: 0, minWidth: 0 }}>
          View transcript
        </Button>
      )}
    </Box>
  );
}

function ChunkTranscriptPanel({ chunk }: { chunk: ContentTaxonomyChunkRow }) {
  const durationSec = Math.max(0, chunk.endTime - chunk.startTime);

  return (
    <Box
      sx={{
        py: 2,
        px: 1.5,
        bgcolor: "action.hover",
        borderRadius: 1,
        maxHeight: 320,
        overflow: "auto",
      }}
    >
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        <Chip size="small" variant="outlined" label={`${formatTime(chunk.startTime)} – ${formatTime(chunk.endTime)}`} />
        <Chip size="small" variant="outlined" label={`${Math.round(durationSec)}s segment`} />
        <Chip size="small" label={actionLabel(chunk.action)} color={actionColor(chunk.action)} />
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
        {chunk.text}
      </Typography>
    </Box>
  );
}

export function buildContentTaxonomySummary(data: ContentTaxonomyChunksResponse) {
  const acceptedCount = data.chunks.filter((c) => c.action === "accept").length;
  const distinctTopics = new Set(
    data.chunks.flatMap((c) => c.resolved?.map((r) => r.slug) ?? []),
  ).size;
  const distinctSubtopics = new Set(
    data.chunks.flatMap((c) => c.resolved?.map((r) => r.subtopic).filter(Boolean) ?? []),
  ).size;
  return {
    totalChunks: data.chunks.length,
    acceptedCount,
    distinctTopics,
    distinctSubtopics,
    episodeTopicCount: data.episodeTopics?.length ?? 0,
    episodeSubtopicCount: data.episodeSubtopics?.length ?? 0,
  };
}

const ContentTaxonomyTopics: React.FC = () => {
  const { contentId } = useParams<{ contentId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["content-taxonomy-chunks", contentId],
    queryFn: () => apiService.getContentTaxonomyChunks(contentId!),
    enabled: !!contentId,
  });

  const summary = useMemo(() => (data ? buildContentTaxonomySummary(data) : null), [data]);
  const subtopicGroups = useMemo(
    () => groupEpisodeSubtopics(data?.episodeSubtopics, data?.episodeTopics),
    [data?.episodeSubtopics, data?.episodeTopics],
  );
  const topicHierarchy = useMemo(
    () => (data ? buildTopicHierarchy(data) : []),
    [data],
  );
  const [expandedChunks, setExpandedChunks] = useState<Set<number>>(() => new Set());
  const [dominanceView, setDominanceView] = useState<DominanceView>("treemap");
  const [dominanceLevel, setDominanceLevel] = useState<DominanceLevel>("topic");
  const [subtopicSelection, setSubtopicSelection] = useState<TreemapSubtopicSelection | null>(
    null,
  );

  const subtopicChunkItems = useMemo(() => {
    if (!data || !subtopicSelection) return [];
    return filterChunksForSubtopic(
      data.chunks,
      subtopicSelection.topicSlug,
      subtopicSelection.subtopicSlug,
    ).map((chunk) => ({
      chunkIndex: chunk.chunkIndex,
      startTime: chunk.startTime,
      endTime: chunk.endTime,
      text: chunk.text,
      contentTitle: data.content.title,
      url: data.content.url,
      source: data.content.source,
    }));
  }, [data, subtopicSelection]);

  const toggleChunkExpanded = (chunkIndex: number) => {
    setExpandedChunks((prev) => {
      const next = new Set(prev);
      if (next.has(chunkIndex)) {
        next.delete(chunkIndex);
      } else {
        next.add(chunkIndex);
      }
      return next;
    });
  };

  const expandAllTranscripts = () => {
    setExpandedChunks(new Set(data?.chunks.map((c) => c.chunkIndex) ?? []));
  };

  const collapseAllTranscripts = () => {
    setExpandedChunks(new Set());
  };

  const backHref = data?.content.podcastId
    ? podcastContentAnalysisPath(data.content.podcastId)
    : "/review";

  const isYoutube = data?.content.source === "youtube" && !!data.content.url;

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load taxonomy chunks for this episode.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Button component={RouterLink} to={backHref} startIcon={<ArrowBackIcon />} size="small">
            Back to review
          </Button>
        </Box>

        <Box>
          <Typography variant="h5" gutterBottom>
            Topic chunks
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {data.content.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Content ID: {data.content._id}
          </Typography>
        </Box>

        {summary && (
          <Stack spacing={1.5}>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip label={`${summary.totalChunks} chunks`} />
              <Chip label={`${summary.acceptedCount} accepted`} color="success" variant="outlined" />
              <Chip label={`${summary.distinctTopics} distinct topics (chunks)`} variant="outlined" />
              {summary.distinctSubtopics > 0 && (
                <Chip
                  label={`${summary.distinctSubtopics} distinct subtopics (chunks)`}
                  variant="outlined"
                />
              )}
              {summary.episodeTopicCount > 0 && (
                <Chip label={`${summary.episodeTopicCount} pool topics (episode)`} variant="outlined" />
              )}
              {summary.episodeSubtopicCount > 0 && (
                <Chip
                  label={`${summary.episodeSubtopicCount} subtopics (episode)`}
                  variant="outlined"
                  color="secondary"
                />
              )}
            </Stack>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={dominanceLevel}
              onChange={(_, value: DominanceLevel | null) => {
                if (value) setDominanceLevel(value);
              }}
              sx={{ display: dominanceView === "network" ? "inline-flex" : "none" }}
            >
              <ToggleButton value="topic">Topics</ToggleButton>
              <ToggleButton value="subtopic" disabled={!hierarchyHasSubtopics(topicHierarchy)}>
                Subtopics
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}

        {topicHierarchy.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="subtitle1">Topic dominance</Typography>
                <Typography variant="body2" color="text.secondary">
                  {dominanceView === "treemap"
                    ? "Click a topic in the treemap to drill into subtopics, then click a subtopic to view chunks."
                    : dominanceLevel === "topic"
                      ? "Node size reflects chunk mentions per main topic."
                      : "Node size reflects chunk mentions per subtopic (colored by parent topic)."}
                </Typography>
              </Box>
              <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="flex-end">
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={dominanceView}
                  onChange={(_, value: DominanceView | null) => {
                    if (value) setDominanceView(value);
                  }}
                >
                  <ToggleButton value="treemap">
                    <GridViewIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Treemap
                  </ToggleButton>
                  <ToggleButton value="network">
                    <AccountTreeIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Network
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            {dominanceView === "treemap" ? (
              <TopicTreemap
                hierarchy={topicHierarchy}
                onSubtopicClick={setSubtopicSelection}
                selectedSubtopicSlug={subtopicSelection?.subtopicSlug ?? null}
                onDrillTopicChange={() => setSubtopicSelection(null)}
              />
            ) : (
              <TopicNetworkGraph hierarchy={topicHierarchy} level={dominanceLevel} />
            )}
          </Paper>
        )}

        <SubtopicChunksPanel
          selection={subtopicSelection}
          chunks={subtopicChunkItems}
          onClose={() => setSubtopicSelection(null)}
        />

        {dominanceLevel === "topic" && (data.episodeTopics?.length ?? topicHierarchy.length) > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Episode topics
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {(data.episodeTopics ?? topicHierarchy.map((t) => ({
                slug: t.slug,
                display: t.display,
                mentions: t.mentions,
                avgConfidence: 0,
              }))).map((topic) => (
                <Tooltip key={topic.slug} title={`${topic.display} [${topic.slug}]`}>
                  <Chip
                    size="small"
                    label={`${topic.display} · ${topic.mentions}`}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Paper>
        )}

        {dominanceLevel === "subtopic" && subtopicGroups.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Episode subtopics
            </Typography>
            <Stack spacing={2}>
              {subtopicGroups.map((group) => (
                <Box key={group.parentTopicSlug}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {group.parentDisplay ?? group.parentTopicSlug}
                    <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                      ({group.parentTopicSlug})
                    </Typography>
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {group.subtopics.map((subtopic) => (
                      <Tooltip
                        key={`${group.parentTopicSlug}-${subtopic.slug}`}
                        title={`${subtopic.display} [${subtopic.slug}]`}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color="secondary"
                          label={`${subtopic.display} · ${subtopic.mentions}`}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {data.chunks.length === 0 ? (
          <Alert severity="info">
            No taxonomy chunk data yet. Run{" "}
            <code>yarn taxonomy:candidates --content-id {data.content._id}</code> in packages/pipelines.
          </Alert>
        ) : (
          <>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={expandAllTranscripts}>
                Expand all transcripts
              </Button>
              <Button size="small" variant="outlined" onClick={collapseAllTranscripts}>
                Collapse all
              </Button>
            </Stack>
            <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" width={40} />
                  <TableCell width={48}>#</TableCell>
                  <TableCell width={120}>Time</TableCell>
                  <TableCell width={100}>Action</TableCell>
                  <TableCell>Transcript</TableCell>
                  <TableCell width={220}>
                    {dominanceLevel === "topic" ? "Topics" : "Subtopics"}
                  </TableCell>
                  <TableCell width={56} align="center">
                    YT
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.chunks.map((chunk) => {
                  const ytUrl = isYoutube
                    ? youtubeWatchUrlFromWatchUrl(data.content.url, chunk.startTime)
                    : null;
                  const isExpanded = expandedChunks.has(chunk.chunkIndex);
                  return (
                    <React.Fragment key={chunk.chunkIndex}>
                      <TableRow hover selected={isExpanded}>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => toggleChunkExpanded(chunk.chunkIndex)}
                            aria-label={isExpanded ? "Hide transcript" : "Show transcript"}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{chunk.chunkIndex + 1}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatTime(chunk.startTime)} – {formatTime(chunk.endTime)}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={actionLabel(chunk.action)} color={actionColor(chunk.action)} />
                        </TableCell>
                        <TableCell>
                          {isExpanded ? (
                            <Typography variant="body2" color="text.secondary">
                              Transcript expanded below
                            </Typography>
                          ) : (
                            <ChunkTranscriptPreview
                              text={chunk.text}
                              onView={() => toggleChunkExpanded(chunk.chunkIndex)}
                            />
                          )}
                        </TableCell>
                      <TableCell>
                        {dominanceLevel === "topic" ? (
                          <TopicChips chunk={chunk} />
                        ) : (
                          <SubtopicChips chunk={chunk} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                          {ytUrl ? (
                            <Tooltip title="Open at this moment on YouTube">
                              <IconButton
                                size="small"
                                color="error"
                                href={ytUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                component="a"
                              >
                                <YouTubeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <ChunkTranscriptPanel chunk={chunk} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}

        {data.content.url && (
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            href={data.content.url}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
          >
            Open episode
          </Button>
        )}
      </Stack>
    </Container>
  );
};

export default ContentTaxonomyTopics;
